// ./react-app/src/scenes/calendar/index.tsx
import { FC, useEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import { EventClickArg, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import Header from '../../components/Header';
import { tokens } from '../../theme';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import fetchData from '../../helpers/fetchData';
import ButtonLink from '../../components/Buttons';
import moment, { now } from 'moment';

interface ICalendarResponse {
  campaign_name: string;
  campaign_type: 'newsletter' | 'manual' | 'sam';
  send_date: string;
  campaign_id: string | number;
  sam_id: number | null;
  recipients: number;
  opens: number;
  ad_clicks: number;
  leads: number;
  group_name: string;
  subject?: string;
  from_name?: string;
  from_email?: string;
  reply_to_email?: string;
  preheader_text?: string;
}

const ColorLegend: React.FC = () => {
  const legends = [
    { label: 'Newsletter (Recurring)', color: '#22c55e' },
    { label: 'Manuelle Kampagnen', color: '#3b82f6' },
    { label: 'Automatisierte SAMs', color: '#a855f7' },
  ];

  return (
    <Box display="flex" justifyContent="center" gap={2} mb={2}>
      {legends.map((legend, index) => (
        <Box key={index} display="flex" alignItems="center" gap={1}>
          <Box width={15} height={15} borderRadius="50%" bgcolor={legend.color} />
          <Typography>{legend.label}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const EventDetails: FC<{ event: ICalendarResponse }> = ({ event }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'P HH:mm', { locale: de });
  };
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('de-DE').format(num);
  };

  return (
    <>
      <Box mb={2}>
        <Typography variant="h5" color={colors.greenAccent[400]}>
          {event.group_name}
        </Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table aria-label="Campaign Event Table" className="calendar-table">
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row">
                Kampagne
              </TableCell>
              <TableCell>{event.campaign_name}</TableCell>
            </TableRow>
            {event.subject && (
              <TableRow>
                <TableCell component="th" scope="row">
                  Betreff
                </TableCell>
                <TableCell>{event.subject}</TableCell>
              </TableRow>
            )}
            {event.from_name && (
              <TableRow>
                <TableCell component="th" scope="row">
                  Absender Name
                </TableCell>
                <TableCell>{event.from_name}</TableCell>
              </TableRow>
            )}
            {event.from_email && (
              <TableRow>
                <TableCell component="th" scope="row">
                  Absender E-Mail
                </TableCell>
                <TableCell>{event.from_email}</TableCell>
              </TableRow>
            )}
            {event.reply_to_email && (
              <TableRow>
                <TableCell component="th" scope="row">
                  Antwort an
                </TableCell>
                <TableCell>{event.reply_to_email}</TableCell>
              </TableRow>
            )}
            {event.preheader_text && (
              <TableRow>
                <TableCell component="th" scope="row">
                  Preheader Text
                </TableCell>
                <TableCell>{event.preheader_text}</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell component="th" scope="row">
                Typ
              </TableCell>
              <TableCell>
                {event.campaign_type === 'newsletter'
                  ? 'Newsletter'
                  : event.campaign_type === 'manual'
                    ? 'Manuelle Kampagne'
                    : 'Automatisierte SAM'}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Versanddatum
              </TableCell>
              <TableCell>{formatDate(event.send_date)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Empfänger
              </TableCell>
              <TableCell>{formatNumber(event.recipients)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Öffner
              </TableCell>
              <TableCell>{formatNumber(event.opens)}</TableCell>
            </TableRow>
            {event.recipients > 0 && (
              <TableRow>
                <TableCell component="th" scope="row">
                  Öffnungsrate
                </TableCell>
                <TableCell>{((event.opens / event.recipients) * 100).toFixed(2)}%</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell component="th" scope="row">
                Ad-Klicks
              </TableCell>
              <TableCell>{formatNumber(event.ad_clicks)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Leads
              </TableCell>
              <TableCell>{formatNumber(event.leads)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Iterable Campaign ID
              </TableCell>
              <TableCell>{event.campaign_id}</TableCell>
            </TableRow>
            {event.sam_id !== null && (
              <TableRow>
                <TableCell component="th" scope="row">
                  SAM
                </TableCell>
                <TableCell>
                  ID: {event.sam_id}
                  <ButtonLink
                    text={'SAM Bearbeiten'}
                    float={'right'}
                    link={
                      'https://www.finanztrends.de/adserver/new_material.php?material_type=ad&action=update&id=' +
                      event.sam_id
                    }
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const getAndProcessData = async () => {
  const options = {
    start_date: new Date(new Date().setMonth(new Date().getMonth() - 3))
      .toISOString()
      .split('T')[0], // 3 months ago
    end_date: new Date(new Date().setDate(new Date().getDate() + 30)).toISOString().split('T')[0], // 30 days in future
  };

  const data = (await fetchData<ICalendarResponse>(
    '/api/iterable-calendar',
    options,
    true,
  )) as ICalendarResponse[];
  console.dir(data);
  return data;
};

const Calendar: FC = () => {
  const [events, setEvents] = useState<EventInput[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<ICalendarResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true); // add a new state variable for loading

  const [open, setOpen] = useState(false);

  const handleEventClick = (clickInfo: EventClickArg) => {
    setSelectedEvent(clickInfo.event.extendedProps as ICalendarResponse);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedEvent(null);
  };

  useEffect(() => {
    getAndProcessData().then((data) => {
      setEvents(
        data.map(
          (item): EventInput => ({
            title: item.campaign_name,
            start: new Date(item.send_date),
            backgroundColor:
              item.campaign_type === 'newsletter'
                ? '#22c55e' // green
                : item.campaign_type === 'manual'
                  ? '#3b82f6' // blue
                  : '#a855f7', // purple for SAMs
            extendedProps: item,
          }),
        ),
      );
      setLoading(false); // set loading to false after the data is fetched
    });
  }, []);

  return (
    <Box m="20px">
      <Header
        title="Iterable E-Mail Marketing Kalender"
        subtitle="Newsletter, Manuelle Kampagnen & Automatisierte SAMs"
      />
      <ColorLegend />
      {/* CALENDAR */}
      {loading ? (
        <Box
          flex="1 1 100%"
          ml="15px"
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="75vh"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box flex="1 1 100%" ml="15px">
          <FullCalendar
            height="75vh"
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth',
            }}
            initialView="dayGridWeek"
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            events={events}
            eventClick={handleEventClick}
            locale={'de'}
            timeZone={'Europe/Berlin'}
            buttonText={{
              today: 'Heute',
              month: 'Monat',
              week: 'Woche',
              day: 'Tag',
              list: 'Liste',
            }}
          />
        </Box>
      )}
      <Dialog onClose={handleClose} open={open}>
        <DialogTitle>Iterable Kampagnen Details</DialogTitle>
        <DialogContent>{selectedEvent && <EventDetails event={selectedEvent} />}</DialogContent>
      </Dialog>
    </Box>
  );
};

export default Calendar;
